import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  SyntheticEvent,
} from "react";
import clsx from "clsx";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { IProject } from "../../../interface/project";
import { IDepartment } from "../../../interface/department";
import { getDepartmentList } from "../../../apis/department";
import { putEditProject, postAddProject } from "../../../apis/project";
import { useSnackbar } from "notistack";
import { MainContext } from "../../../context/main";
import { LangContext } from "../../../context/lang";

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    height: `calc(100% - ${theme.spacing(4)}px)`,
    width: `calc(100% - ${theme.spacing(4)}px)`,
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    height: `calc(100% - ${theme.spacing(6)}px)`,
  },
  divForm: { height: `calc(100% - ${theme.spacing(4.5)}px)` },
  label: { fontFamily: ["Montserrat-Medium", "Kanit-Medium"].join(",") },
  txtBtn: { textTransform: "unset" },
}));

const ProjectDetail = () => {
  const classes = useStyle();
  const [param, setparam] = useState<IProject>({
    customer_address: "",
    customer_name: "",
    detail: "",
    status: 0,
    work_order_no: "",
  });
  const [process, setprocess] = useState<boolean>(false);
  const [departments, setdepartments] = useState<IDepartment[]>([]);

  const history = useHistory();
  const location = useLocation();

  const { userInfo } = useContext(MainContext);
  const { lang } = useContext(LangContext);

  const { enqueueSnackbar } = useSnackbar();

  const { state } = location;

  const initParam = useCallback(async () => {
    if (state && typeof state === "string") {
      const stateParam = JSON.parse(state);
      setparam(stateParam);
    } else {
      if (userInfo?.mea) {
        setparam({
          customer_address: "",
          customer_name: "",
          detail: "",
          status: 0,
          work_order_no: "",
          department_id: userInfo.mea.department_id,
        });
      }
    }
  }, [state, userInfo]);

  useEffect(() => {
    const abortController = new AbortController();
    getDepartmentList(abortController).then((rsDepartments: any) => {
      if (rsDepartments?.data) {
        setdepartments(rsDepartments.data);
        initParam();
      }
    });

    return () => {
      abortController.abort();
    };
  }, [initParam]);

  const _onChangeString = (e: React.ChangeEvent<HTMLInputElement>) => {
    setparam({ ...param, [e.currentTarget.name]: e.currentTarget.value });
  };

  const _onChangeSelect = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const name = event.target.name as keyof typeof param;
    setparam({
      ...param,
      [name]: event.target.value,
    });
  };

  const _onSave = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (
      param.work_order_no !== "" &&
      param.customer_name !== "" &&
      param.department_id
    ) {
      setprocess(true);
      if (state) {
        const rsUpdateProject = await putEditProject(param);
        setprocess(false);
        rsUpdateProject.status?.code === 200
          ? history.push(`/app/design/projects`)
          : rsUpdateProject.status?.message
          ? enqueueSnackbar(rsUpdateProject.status.message, {
              variant: "error",
            })
          : enqueueSnackbar("Oops, something went wrong", { variant: "error" });
      } else {
        const rsAddProject = await postAddProject(param);
        setprocess(false);
        rsAddProject.status?.code === 201
          ? history.push(`/app/design/projects`)
          : rsAddProject.status?.message
          ? enqueueSnackbar(rsAddProject.status.message, { variant: "error" })
          : enqueueSnackbar("Oops, something went wrong", { variant: "error" });
      }
    }
  };

  const _disableDepartment = () => {
    if (userInfo?.role === "admin") {
      return false;
    } else if (
      userInfo?.role === "officer" ||
      userInfo?.role === "officer_head"
    ) {
      return true;
    } else {
      if (state && param.department_id) {
        return true;
      } else {
        return false;
      }
    }
  };

  return (
    <div className={classes.root}>
      <Paper elevation={2} className={classes.paper}>
        <form onSubmit={_onSave}>
          <div className={classes.divForm}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="subtitle1" className={classes.label}>
                  {lang.data.workOrderNo}
                </Typography>
                <TextField
                  variant="outlined"
                  name="work_order_no"
                  value={param.work_order_no}
                  onChange={_onChangeString}
                  fullWidth
                  margin="dense"
                  error={param.work_order_no === ""}
                  helperText={
                    param.work_order_no === ""
                      ? `${
                          lang.data.required
                        } ${lang.data.workOrderNo.toLowerCase()}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle1" className={classes.label}>
                  {lang.data.customerName}
                </Typography>
                <TextField
                  variant="outlined"
                  name="customer_name"
                  value={param.customer_name}
                  onChange={_onChangeString}
                  fullWidth
                  margin="dense"
                  placeholder='as "ชื่อโครงการ", "ชื่อผู้ร้องขอ"'
                  error={param.customer_name === ""}
                  helperText={
                    param.customer_name === ""
                      ? `${
                          lang.data.required
                        } ${lang.data.customerName.toLowerCase()}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle1" className={classes.label}>
                  {lang.data.department}
                </Typography>
                <FormControl fullWidth error={!Boolean(param.department_id)}>
                  <Select
                    variant="outlined"
                    name="department_id"
                    fullWidth
                    margin="dense"
                    style={{ marginTop: 8, marginBottom: 4 }}
                    value={param.department_id ? param.department_id : ""}
                    onChange={_onChangeSelect}
                    disabled={_disableDepartment()}
                  >
                    {departments.map((d: IDepartment) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {!Boolean(param.department_id) && (
                    <FormHelperText>{`${lang.data.required} ${lang.data.department}`}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.label}>
                  {lang.data.detail}
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={5}
                  name="detail"
                  value={param.detail}
                  onChange={_onChangeString}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.label}>
                  {lang.data.customerAddress}
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={5}
                  name="customer_address"
                  value={param.customer_address}
                  onChange={_onChangeString}
                />
              </Grid>
            </Grid>
          </div>
          <Grid container spacing={2} justify="flex-end">
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  history.push(`/app/design/projects`);
                }}
                className={clsx(classes.label, classes.txtBtn)}
                disabled={process}
              >
                {lang.data.cancel}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={process}
                className={clsx(classes.label, classes.txtBtn)}
              >
                {state ? lang.data.update : lang.data.create}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default ProjectDetail;
